import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { useSucursal } from '../../utils/sucursalContext';
import ConfigurarHorariosModal from './components/ConfigurarHorariosModal';
import EditarCuposModal from './components/EditarCuposModal';
import AgregarFeriadoModal from './components/AgregarFeriadoModal';
import { supabase } from '../../lib/supabase';

type DiaSemana = 'Lunes' | 'Martes' | 'Miércoles' | 'Jueves' | 'Viernes' | 'Sábado' | 'Domingo';
type DiaSemanaKey = 'lunes' | 'martes' | 'miercoles' | 'jueves' | 'viernes' | 'sabado' | 'domingo';

type HorarioDia = {
  dia: DiaSemana;
  apertura: string;
  cierre: string;
  activo: boolean;
};

type HorariosConfigDia = {
  inicio: string;
  fin: string;
  activo: boolean;
};

type HorariosConfig = Record<DiaSemanaKey, HorariosConfigDia>;

type Feriado = {
  id: string;
  fecha: string;
  nombre: string;
  tipo: string;
};

const formatDate = (d: Date) => d.toISOString().slice(0, 10);

const addDays = (d: Date, days: number) => {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
};

// Algoritmo de Meeus/Jones/Butcher para Pascua (calendario gregoriano)
const easterSunday = (year: number) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
};

const feriadosLegalesChileByYear = (year: number) => {
  const easter = easterSunday(year);
  const fixed = (month: number, day: number) =>
    formatDate(new Date(year, month - 1, day));

  return [
    { fecha: fixed(1, 1), nombre: 'Año Nuevo', tipo: 'Feriado Legal' },
    { fecha: formatDate(addDays(easter, -2)), nombre: 'Viernes Santo', tipo: 'Feriado Legal' },
    { fecha: formatDate(addDays(easter, -1)), nombre: 'Sábado Santo', tipo: 'Feriado Legal' },
    { fecha: fixed(5, 1), nombre: 'Día del Trabajo', tipo: 'Feriado Legal' },
    { fecha: fixed(5, 21), nombre: 'Día de las Glorias Navales', tipo: 'Feriado Legal' },
    { fecha: fixed(6, 29), nombre: 'San Pedro y San Pablo', tipo: 'Feriado Legal' },
    { fecha: fixed(7, 16), nombre: 'Día de la Virgen del Carmen', tipo: 'Feriado Legal' },
    { fecha: fixed(8, 15), nombre: 'Asunción de la Virgen', tipo: 'Feriado Legal' },
    { fecha: fixed(9, 18), nombre: 'Día de la Independencia Nacional', tipo: 'Feriado Legal' },
    { fecha: fixed(9, 19), nombre: 'Día de las Glorias del Ejército', tipo: 'Feriado Legal' },
    { fecha: fixed(10, 12), nombre: 'Encuentro de Dos Mundos', tipo: 'Feriado Legal' },
    { fecha: fixed(10, 31), nombre: 'Día de las Iglesias Evangélicas y Protestantes', tipo: 'Feriado Legal' },
    { fecha: fixed(11, 1), nombre: 'Día de Todos los Santos', tipo: 'Feriado Legal' },
    { fecha: fixed(12, 8), nombre: 'Inmaculada Concepción', tipo: 'Feriado Legal' },
    { fecha: fixed(12, 25), nombre: 'Navidad', tipo: 'Feriado Legal' },
  ];
};
const DIA_SEMANA_KEYS: DiaSemanaKey[] = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
const DIA_LABEL_BY_KEY: Record<DiaSemanaKey, DiaSemana> = {
  lunes: 'Lunes',
  martes: 'Martes',
  miercoles: 'Miércoles',
  jueves: 'Jueves',
  viernes: 'Viernes',
  sabado: 'Sábado',
  domingo: 'Domingo',
};
const DIA_LABEL_VARIANTS: Record<DiaSemanaKey, string[]> = {
  lunes: ['Lunes', 'lunes'],
  martes: ['Martes', 'martes'],
  // Incluye variantes históricas para no romper datos ya guardados.
  miercoles: ['Miércoles', 'Miercoles', 'miércoles', 'miercoles', 'MiÃ©rcoles', 'miÃ©rcoles'],
  jueves: ['Jueves', 'jueves'],
  viernes: ['Viernes', 'viernes'],
  // Incluye variantes históricas para no romper datos ya guardados.
  sabado: ['Sábado', 'Sabado', 'sábado', 'sabado', 'SÃ¡bado', 'sÃ¡bado'],
  domingo: ['Domingo', 'domingo'],
};
const buildDefaultHorariosConfig = (): HorariosConfig => ({
  lunes: { inicio: '06:00', fin: '22:00', activo: true },
  martes: { inicio: '06:00', fin: '22:00', activo: true },
  miercoles: { inicio: '06:00', fin: '22:00', activo: true },
  jueves: { inicio: '06:00', fin: '22:00', activo: true },
  viernes: { inicio: '06:00', fin: '22:00', activo: true },
  sabado: { inicio: '06:00', fin: '22:00', activo: true },
  domingo: { inicio: '06:00', fin: '22:00', activo: true },
});
const buildHorariosConfigFromSucursal = (rawHorarios: any): HorariosConfig => {
  const base = buildDefaultHorariosConfig();
  const raw = (rawHorarios || {}) as Record<string, { apertura?: string; cierre?: string; cerrado?: boolean }>;
  for (const key of DIA_SEMANA_KEYS) {
    const row = raw[key];
    if (!row) continue;
    base[key] = {
      inicio: row.apertura || '06:00',
      fin: row.cierre || '22:00',
      activo: !row.cerrado,
    };
  }
  return base;
};
const pickCupoByDay = (dias: Record<string, number> | undefined, key: DiaSemanaKey): number => {
  if (!dias) return 0;
  for (const label of DIA_LABEL_VARIANTS[key]) {
    if (dias[label] !== undefined) return dias[label];
  }
  return 0;
};

const normalizeCuposByConfig = (
  sourceCupos: Record<string, Record<string, number>>,
  config: HorariosConfig
): Record<string, Record<string, number>> => {
  const normalized: Record<string, Record<string, number>> = {};

  for (const [bloque, dias] of Object.entries(sourceCupos)) {
    normalized[bloque] = {};

    for (const key of DIA_SEMANA_KEYS) {
      const diaLabel = DIA_LABEL_BY_KEY[key];
      const dayActive = config[key]?.activo ?? true;
      const value = dayActive ? pickCupoByDay(dias, key) : 0;
      normalized[bloque][diaLabel] = value;
    }
  }

  return normalized;
};
export default function HorariosPage() {
  const { sucursalActiva, refreshSucursales } = useSucursal();
  const [activeTab, setActiveTab] = useState<'horarios' | 'cupos' | 'feriados'>('horarios');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCuposModal, setShowCuposModal] = useState(false);
  const [showFeriadoModal, setShowFeriadoModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState('');

  const [horarios, setHorarios] = useState<any[]>([]);
  const [horariosConfig, setHorariosConfig] = useState<HorariosConfig>(buildDefaultHorariosConfig());
  const [cupos, setCupos] = useState<Record<string, Record<string, number>>>({});
  const [feriados, setFeriados] = useState<any[]>([]);

  // Días de la semana para mostrar
  const diasSemana: DiaSemana[] = DIA_SEMANA_KEYS.map((key) => DIA_LABEL_BY_KEY[key]);

  // Horarios por día (estructura para mostrar)
  const horariosPorDia: HorarioDia[] = DIA_SEMANA_KEYS.map((key) => ({
    dia: DIA_LABEL_BY_KEY[key],
    apertura: horariosConfig[key]?.inicio || '06:00',
    cierre: horariosConfig[key]?.fin || '22:00',
    activo: horariosConfig[key]?.activo ?? true,
  }));

  // Bloques horarios estándar
  const bloquesHorarios = [
    '06:00 - 07:00', '07:00 - 08:00', '08:00 - 09:00', '09:00 - 10:00',
    '10:00 - 11:00', '11:00 - 12:00', '12:00 - 13:00', '13:00 - 14:00',
    '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00',
    '18:00 - 19:00', '19:00 - 20:00', '20:00 - 21:00', '21:00 - 22:00'
  ];

  // Estructura de cupos por horario para la tabla
  const cuposPorHorario: Record<string, Record<string, number>> = {};
  bloquesHorarios.forEach(bloque => {
    cuposPorHorario[bloque] = {};
    DIA_SEMANA_KEYS.forEach((key) => {
      const dia = DIA_LABEL_BY_KEY[key];
      const isActive = horariosConfig[key]?.activo ?? true;
      cuposPorHorario[bloque][dia] = isActive ? pickCupoByDay(cupos[bloque], key) : 0;
    });
  });

  // Cargar datos desde Supabase
  useEffect(() => {
    cargarDatos();
  }, [sucursalActiva?.id]);

  useEffect(() => {
    if (!sucursalActiva) return;
    setHorariosConfig(buildHorariosConfigFromSucursal(sucursalActiva.horarios));
  }, [sucursalActiva?.id, sucursalActiva?.horarios]);

  const cargarDatos = async () => {
    if (!sucursalActiva?.id) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Cargar horarios
      const { data: horariosData } = await supabase
        .from('horarios')
        .select('*')
        .eq('sucursal_id', sucursalActiva.id)
        .order('hora_inicio');

      if (horariosData) {
        setHorarios(horariosData);
      }

      // Cargar configuración de cupos
      const { data: cuposData } = await supabase
        .from('configuracion_cupos')
        .select('*')
        .eq('sucursal_id', sucursalActiva.id);

      if (cuposData) {
        // Convertir array a estructura de objeto
        const cuposObj: Record<string, Record<string, number>> = {};
        cuposData.forEach((item: any) => {
          if (!cuposObj[item.bloque_horario]) {
            cuposObj[item.bloque_horario] = {};
          }
          cuposObj[item.bloque_horario][item.dia_semana] = item.cupos;
        });
        setCupos(cuposObj);
      }

      const currentYear = new Date().getFullYear();
      const startYear = `${currentYear}-01-01`;
      const nextYear = `${currentYear + 1}-01-01`;

      // Asegurar feriados legales del año actual (sin pisar manuales)
      await asegurarFeriadosLegalesAnio(currentYear);

      // Cargar feriados activos del año actual
      const { data: feriadosData } = await supabase
        .from('feriados')
        .select('*')
        .eq('sucursal_id', sucursalActiva.id)
        .eq('activo', true)
        .gte('fecha', startYear)
        .lt('fecha', nextYear)
        .order('fecha');

      setFeriados(feriadosData || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const asegurarFeriadosLegalesAnio = async (year: number) => {
    if (!sucursalActiva?.id) return;

    const startYear = `${year}-01-01`;
    const nextYear = `${year + 1}-01-01`;
    const feriadosLegales = feriadosLegalesChileByYear(year);

    const { data: existentes, error: errorExistentes } = await supabase
      .from('feriados')
      .select('fecha,nombre')
      .eq('sucursal_id', sucursalActiva.id)
      .gte('fecha', startYear)
      .lt('fecha', nextYear);

    if (errorExistentes) throw errorExistentes;

    const existentesSet = new Set(
      (existentes || []).map((f: any) => `${f.fecha}|${String(f.nombre || '').trim().toLowerCase()}`)
    );

    const faltantes = feriadosLegales
      .filter((f) => !existentesSet.has(`${f.fecha}|${f.nombre.trim().toLowerCase()}`))
      .map((f) => ({
        sucursal_id: sucursalActiva.id,
        fecha: f.fecha,
        nombre: f.nombre,
        tipo: f.tipo,
        activo: true,
      }));

    if (faltantes.length === 0) return;

    const { error: insertError } = await supabase
      .from('feriados')
      .insert(faltantes);

    if (insertError) throw insertError;
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const persistirCupos = async (cuposInput: Record<string, Record<string, number>>, config: HorariosConfig) => {
    if (!sucursalActiva?.id) return { registrosGuardados: 0, cuposNormalizados: {} as Record<string, Record<string, number>> };

    const cuposNormalizados = normalizeCuposByConfig(cuposInput, config);

    const { error: deleteError } = await supabase
      .from('configuracion_cupos')
      .delete()
      .eq('sucursal_id', sucursalActiva.id);

    if (deleteError) throw deleteError;

    const registros: any[] = [];
    Object.entries(cuposNormalizados).forEach(([bloque, dias]) => {
      DIA_SEMANA_KEYS.forEach((key) => {
        const diaLabel = DIA_LABEL_BY_KEY[key];
        const cuposVal = dias[diaLabel] || 0;
        if (cuposVal > 0) {
          registros.push({
            sucursal_id: sucursalActiva.id,
            bloque_horario: bloque,
            dia_semana: diaLabel,
            cupos: cuposVal,
            activo: true,
          });
        }
      });
    });

    if (registros.length > 0) {
      const { error } = await supabase
        .from('configuracion_cupos')
        .insert(registros)
        .select();

      if (error) throw error;
    }

    return { registrosGuardados: registros.length, cuposNormalizados };
  };

  const handleGuardarCupos = async (nuevosCupos: Record<string, Record<string, number>>) => {
    if (!sucursalActiva?.id) return;

    try {
      const { registrosGuardados, cuposNormalizados } = await persistirCupos(nuevosCupos, horariosConfig);

      if (registrosGuardados === 0) {
        setCupos({});
        setShowCuposModal(false);
        showSuccess('Cupos limpiados correctamente');
        return;
      }
      setCupos(cuposNormalizados);
      setShowCuposModal(false);
      await cargarDatos();
      showSuccess(`${registrosGuardados} cupos guardados para ${sucursalActiva.nombre}`);
    } catch (error: any) {
      console.error('Error al guardar cupos:', error);
    }
  };

  const handleGuardarHorarios = async (nuevosHorarios: Record<string, { inicio: string; fin: string; activo: boolean }>) => {
    if (!sucursalActiva?.id) return;

    try {
      const horariosTipados: HorariosConfig = buildDefaultHorariosConfig();
      DIA_SEMANA_KEYS.forEach((key) => {
        const incoming = nuevosHorarios[key];
        horariosTipados[key] = {
          inicio: incoming?.inicio || '06:00',
          fin: incoming?.fin || '22:00',
          activo: incoming?.activo ?? true,
        };
      });

      const payloadHorarios = DIA_SEMANA_KEYS.reduce((acc, key) => {
        acc[key] = {
          apertura: horariosTipados[key].inicio,
          cierre: horariosTipados[key].fin,
          cerrado: !horariosTipados[key].activo,
        };
        return acc;
      }, {} as Record<string, { apertura: string; cierre: string; cerrado: boolean }>);

      const { error: updateSucursalError } = await supabase
        .from('sucursales')
        .update({
          horarios: payloadHorarios,
          updated_at: new Date().toISOString(),
        })
        .eq('id', sucursalActiva.id);

      if (updateSucursalError) throw updateSucursalError;

      const { cuposNormalizados } = await persistirCupos(cupos, horariosTipados);

      setHorariosConfig(horariosTipados);
      setCupos(cuposNormalizados);
      setShowConfigModal(false);
      await refreshSucursales();
      await cargarDatos();
      showSuccess('Horarios guardados y cupos sincronizados correctamente');
    } catch (error) {
      console.error('Error guardando horarios:', error);
    }
  };

  const handleAgregarFeriado = async (feriado: Omit<Feriado, 'id'>) => {
    if (!sucursalActiva?.id) {
      console.error('No hay sucursal seleccionada');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('feriados')
        .insert([{
          sucursal_id: sucursalActiva.id,
          fecha: feriado.fecha,
          nombre: feriado.nombre,
          tipo: feriado.tipo,
          activo: true
        }])
        .select()
        .single();

      if (error) throw error;

      setFeriados([...feriados, data]);
      setShowFeriadoModal(false);
      showSuccess('Feriado agregado correctamente');
    } catch (error: any) {
      console.error('Error al agregar feriado:', error);
      alert('Error al guardar el feriado. Por favor intenta nuevamente.');
    }
  };

  const handleEliminarFeriado = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este feriado?')) return;

    try {
      const { error } = await supabase
        .from('feriados')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setFeriados(feriados.filter(f => f.id !== id));
      showSuccess('Feriado eliminado correctamente');
    } catch (error: any) {
      console.error('Error al eliminar feriado:', error);
      alert('Error al eliminar el feriado. Por favor intenta nuevamente.');
    }
  };

  const getTipoColor = (tipo: string) => {
    const tipoLower = tipo.toLowerCase();
    if (tipoLower.includes('feriado') || tipoLower.includes('nacional')) return 'bg-red-100 text-red-700';
    if (tipoLower.includes('cierre') || tipoLower.includes('especial')) return 'bg-orange-100 text-orange-700';
    if (tipoLower.includes('mantenimiento')) return 'bg-yellow-100 text-yellow-700';
    if (tipoLower.includes('evento') || tipoLower.includes('privado')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getTipoTexto = (tipo: string) => {
    return tipo;
  };

  const convertirHorariosAConfig = () => {
    return { ...horariosConfig };
  };

  const convertirCuposABloques = () => {
    return bloquesHorarios.map((bloque, index) => ({
      id: index + 1,
      hora: bloque,
      lunes: horariosConfig.lunes.activo ? pickCupoByDay(cupos[bloque], 'lunes') : 0,
      martes: horariosConfig.martes.activo ? pickCupoByDay(cupos[bloque], 'martes') : 0,
      miercoles: horariosConfig.miercoles.activo ? pickCupoByDay(cupos[bloque], 'miercoles') : 0,
      jueves: horariosConfig.jueves.activo ? pickCupoByDay(cupos[bloque], 'jueves') : 0,
      viernes: horariosConfig.viernes.activo ? pickCupoByDay(cupos[bloque], 'viernes') : 0,
      sabado: horariosConfig.sabado.activo ? pickCupoByDay(cupos[bloque], 'sabado') : 0,
      domingo: horariosConfig.domingo.activo ? pickCupoByDay(cupos[bloque], 'domingo') : 0,
    }));
  };

  // Contar cupos totales configurados
  const totalCuposConfigurados = Object.values(cupos).reduce((total, dias) => {
    return total + Object.values(dias).reduce((sum, c) => sum + c, 0);
  }, 0);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin"></i>
            <p className="mt-4 text-gray-600">Cargando configuración...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Success Toast */}
        {successMsg && (
          <div className="fixed top-6 right-6 z-50 bg-green-600 text-white px-5 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
            <i className="ri-check-line text-lg"></i>
            <span className="text-sm font-medium">{successMsg}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Horarios y Cupos</h1>
            {sucursalActiva && (
              <p className="text-sm text-gray-500 mt-1">
                <i className="ri-building-line mr-1"></i>
                Configurando para: <span className="font-semibold text-teal-600">{sucursalActiva.nombre}</span>
              </p>
            )}
          </div>
          {totalCuposConfigurados > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-teal-50 rounded-lg border border-teal-200">
              <i className="ri-group-line text-teal-600"></i>
              <span className="text-sm text-teal-700 font-medium">{totalCuposConfigurados} cupos totales configurados</span>
            </div>
          )}
        </div>

        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('horarios')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'horarios'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Horarios y Cupos
          </button>
          <button
            onClick={() => setActiveTab('feriados')}
            className={`px-6 py-3 font-medium transition-colors whitespace-nowrap cursor-pointer ${
              activeTab === 'feriados'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Feriados y Cierres
          </button>
        </div>

        {activeTab === 'horarios' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Horarios por Día</h2>
                <button
                  onClick={() => setShowConfigModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-settings-3-line mr-2"></i>
                  Configurar Horarios
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {horariosPorDia.map((horario) => (
                  <div
                    key={horario.dia}
                    className={`p-4 rounded-lg border ${
                      horario.activo
                        ? 'bg-teal-50 border-teal-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="text-center">
                      <p className="font-semibold text-gray-900 mb-2">{horario.dia}</p>
                      {horario.activo ? (
                        <>
                          <div className="flex items-center justify-center text-sm text-gray-600 mb-1">
                            <i className="ri-time-line mr-1"></i>
                            {horario.apertura}
                          </div>
                          <div className="flex items-center justify-center text-sm text-gray-600">
                            <i className="ri-time-line mr-1"></i>
                            {horario.cierre}
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-500">Cerrado</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">Cupos por Horario</h2>
                <button
                  onClick={() => setShowCuposModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line mr-2"></i>
                  Editar Cupos
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">HORARIO</th>
                      {horariosPorDia.map((h) => (
                        <th key={h.dia} className="text-center py-3 px-4 font-semibold text-gray-700">
                          {h.dia.substring(0, 3).toUpperCase()}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(cuposPorHorario).map(([horario, cuposRow]) => (
                      <tr key={horario} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-700">{horario}</td>
                        {horariosPorDia.map((h) => (
                          <td key={h.dia} className="py-3 px-4 text-center">
                            {cuposRow[h.dia] > 0 ? (
                              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-teal-100 text-teal-700 font-semibold text-sm">
                                {cuposRow[h.dia]}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'feriados' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Feriados y Cierres Especiales</h2>
                <p className="text-sm text-gray-500 mt-1">Incluye los feriados legales de Chile del año actual y cierres manuales</p>
              </div>
              <button
                onClick={() => setShowFeriadoModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line mr-2"></i>
                Agregar Feriado
              </button>
            </div>

            {feriados.length === 0 ? (
              <div className="text-center py-12">
                <i className="ri-calendar-close-line text-5xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">No hay feriados o cierres registrados</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Fecha</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Nombre</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Tipo</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feriados.map((feriado) => (
                      <tr key={feriado.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 text-sm text-gray-700">
                          {new Date(feriado.fecha + 'T00:00:00').toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-900 font-medium">{feriado.nombre}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getTipoColor(feriado.tipo)}`}>
                            {getTipoTexto(feriado.tipo)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <button
                            onClick={() => handleEliminarFeriado(feriado.id)}
                            className="text-red-600 hover:text-red-700 cursor-pointer"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {showConfigModal && (
          <ConfigurarHorariosModal
            horariosIniciales={convertirHorariosAConfig()}
            onSave={handleGuardarHorarios}
            onClose={() => setShowConfigModal(false)}
          />
        )}

        {showCuposModal && (
          <EditarCuposModal
            bloquesIniciales={convertirCuposABloques()}
            diasActivos={DIA_SEMANA_KEYS.filter((key) => horariosConfig[key]?.activo ?? false)}
            onSave={handleGuardarCupos}
            onClose={() => setShowCuposModal(false)}
          />
        )}

        {showFeriadoModal && (
          <AgregarFeriadoModal
            onSave={handleAgregarFeriado}
            onClose={() => setShowFeriadoModal(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
}

